import React, { createContext, useContext, useState, useEffect } from 'react';
import { createTheme, ThemeProvider as MUIThemeProvider, CssBaseline } from '@mui/material';

const ThemeContext = createContext();

export const useTheme = () => useContext(ThemeContext);

// Default FlockTrade colors (fallback)
const DEFAULT_COLORS = {
  primary: '#ff6b00',
  secondary: '#050810',
  accent: '#ff6b00',
  background: '#ffffff',
  text: '#1A2027',
};

const DEFAULT_FONTS = { heading: 'Inter Tight', body: 'Inter Tight' };

export const ThemeProvider = ({ children }) => {
  const [darkMode, setDarkMode] = useState(
    localStorage.getItem('darkMode') !== 'false'
  );
  const [tenantConfig, setTenantConfig] = useState(() => {
    // Load from sessionStorage for instant render
    try {
      const cached = sessionStorage.getItem('tenantConfig');
      return cached ? JSON.parse(cached) : null;
    } catch { return null; }
  });

  // Fetch tenant config on mount
  useEffect(() => {
    fetch('/api/tenant/config')
      .then((res) => res.ok ? res.json() : null)
      .then((data) => {
        if (data) {
          setTenantConfig(data);
          sessionStorage.setItem('tenantConfig', JSON.stringify(data));
        }
      })
      .catch(() => {}); // silent fail — use defaults
  }, []);

  // Update favicon, title, and fonts when tenant config changes
  useEffect(() => {
    if (!tenantConfig) return;

    // Page title
    if (tenantConfig.name && tenantConfig.slug !== 'flocktrade') {
      document.title = tenantConfig.name;
    }

    // Favicon
    if (tenantConfig.favicon) {
      const link = document.querySelector("link[rel~='icon']") || document.createElement('link');
      link.rel = 'icon';
      link.href = tenantConfig.favicon;
      if (!link.parentNode) document.head.appendChild(link);
    }

    // Load Google Fonts if tenant has custom fonts
    const tf = tenantConfig.fonts;
    if (tf && (tf.heading !== 'Roboto' || tf.body !== 'Roboto')) {
      const families = [...new Set([tf.heading, tf.body].filter((f) => f && f !== 'Roboto'))];
      if (families.length > 0) {
        const id = 'tenant-google-fonts';
        if (!document.getElementById(id)) {
          const link = document.createElement('link');
          link.id = id;
          link.rel = 'stylesheet';
          link.href = `https://fonts.googleapis.com/css2?${families.map((f) => `family=${f.replace(/ /g, '+')}:wght@400;500;600;700`).join('&')}&display=swap`;
          document.head.appendChild(link);
        }
      }
    }

    // Theme-color meta tag
    if (tenantConfig.colors?.primary) {
      const meta = document.querySelector('meta[name="theme-color"]');
      if (meta) meta.content = tenantConfig.colors.secondary || tenantConfig.colors.primary;
    }
  }, [tenantConfig]);

  const toggleDarkMode = () => {
    setDarkMode((prev) => {
      const newValue = !prev;
      localStorage.setItem('darkMode', newValue);
      return newValue;
    });
  };

  const colors = tenantConfig?.colors || DEFAULT_COLORS;
  const fonts = tenantConfig?.fonts || DEFAULT_FONTS;

  const theme = createTheme({
    palette: {
      mode: darkMode ? 'dark' : 'light',
      primary: { main: colors.primary },
      secondary: { main: colors.accent || '#ff6b00' },
      background: {
        default: darkMode ? colors.secondary : '#f5f5f5',
        paper: darkMode ? '#0a0e17' : colors.background,
      },
      text: {
        primary: darkMode ? '#c8d4e8' : colors.text,
        secondary: darkMode ? '#7a8699' : '#616161',
      },
      // Never leave this undefined: MUI 6 evaluates every styled variant eagerly,
      // and Divider's `light` variant calls alpha(theme.palette.divider). With
      // undefined that throws and unmounts the whole app (blank white page) on
      // any route that renders a <Divider> in light mode (Sentiment, Forex Calendar).
      divider: darkMode ? '#1e2a3f' : 'rgba(0, 0, 0, 0.12)',
    },
    typography: {
      fontFamily: `"${fonts.body}", "Inter Tight", -apple-system, sans-serif`,
      h1: { fontFamily: `"${fonts.heading}", "Inter Tight", sans-serif`, fontWeight: 700 },
      h2: { fontFamily: `"${fonts.heading}", "Inter Tight", sans-serif`, fontWeight: 700 },
      h3: { fontFamily: `"${fonts.heading}", "Inter Tight", sans-serif`, fontWeight: 700 },
      h4: { fontFamily: `"${fonts.heading}", "Inter Tight", sans-serif`, fontWeight: 700 },
      h5: { fontFamily: `"${fonts.heading}", "Inter Tight", sans-serif`, fontWeight: 600 },
      h6: { fontFamily: `"${fonts.heading}", "Inter Tight", sans-serif`, fontWeight: 600 },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            fontFamily: `"Inter Tight", -apple-system, sans-serif`,
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 0,
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: '12px',
            fontWeight: 500,
            letterSpacing: '1.5px',
            textTransform: 'uppercase',
            transition: 'all 0.2s',
          },
          contained: {
            backgroundColor: '#ff6b00',
            color: '#050810',
            '&:hover': {
              backgroundColor: '#ffffff',
              boxShadow: '0 0 20px rgba(255, 107, 0, 0.15)',
            },
          },
          outlined: {
            borderColor: '#2a3d5c',
            color: '#ffffff',
            '&:hover': {
              borderColor: '#ff6b00',
              color: '#ff6b00',
              backgroundColor: 'transparent',
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 0,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: 0,
          },
        },
      },
    },
  });

  return (
    <ThemeContext.Provider value={{
      darkMode,
      toggleDarkMode,
      tenantConfig,
    }}>
      <MUIThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MUIThemeProvider>
    </ThemeContext.Provider>
  );
};
